// Helper Utility Functions

/**
 * Format date to readable string
 * @param {Date|Timestamp} date - Date object or Firebase Timestamp
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  
  let dateObj;
  if (date.toDate) {
    // Firebase Timestamp
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return 'N/A';
  }
  
  return dateObj.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format currency to Indian Rupees
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (typeof amount !== 'number') return '₹0';
  return '₹' + amount.toLocaleString('en-IN');
};

/**
 * Generate unique token number
 * @returns {string} Token number in format VIP-YYYY-XXXX
 */
export const generateTokenNumber = () => {
  const year = new Date().getFullYear();
  const random = Math.floor(1000 + Math.random() * 9000);
  return `VIP-${year}-${random}`;
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (Indian format)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid
 */
export const isValidPhone = (phone) => {
  const phoneRegex = /^[6-9]\d{9}$/;
  return phoneRegex.test(phone.replace(/\s+/g, ''));
};

/**
 * Get Firebase error message
 * @param {string} errorCode - Firebase error code
 * @returns {string} User-friendly error message
 */
export const getFirebaseErrorMessage = (errorCode) => {
  const errorMessages = {
    'auth/invalid-email': 'Please enter a valid email address',
    'auth/user-disabled': 'This account has been disabled',
    'auth/user-not-found': 'No account found with this email',
    'auth/wrong-password': 'Incorrect password',
    'auth/email-already-in-use': 'This email is already registered',
    'auth/weak-password': 'Password must be at least 6 characters',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later',
    'auth/network-request-failed': 'Network error. Please check your connection',
    'permission-denied': 'You do not have permission to perform this action',
  };
  
  return errorMessages[errorCode] || 'An unexpected error occurred';
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Generate avatar URL from name
 * @param {string} name - User name
 * @returns {string} Avatar URL
 */
export const getAvatarUrl = (name) => {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=667eea&color=fff&size=128`;
};

/**
 * Calculate army fund donation
 * @param {number} prizeAmount - Total prize amount
 * @param {number} percentage - Donation percentage
 * @returns {object} Object with donation and actual prize
 */
export const calculateArmyFund = (prizeAmount, percentage) => {
  const donation = Math.round((prizeAmount * percentage) / 100);
  const actualPrize = prizeAmount - donation;
  
  return {
    donation,
    actualPrize,
    percentage
  };
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy:', error);
    return false;
  }
};

/**
 * Download file from URL
 * @param {string} url - File URL
 * @param {string} filename - Filename for download
 */
export const downloadFile = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Share content using Web Share API
 * @param {object} data - Share data {title, text, url}
 * @returns {Promise<boolean>} Success status
 */
export const shareContent = async (data) => {
  if (navigator.share) {
    try {
      await navigator.share(data);
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share failed:', error);
      }
      return false;
    }
  }
  return false;
};

/**
 * Check if user is admin
 * @param {string} uid - User ID
 * @param {string} email - User email
 * @returns {boolean} True if admin
 */
export const isAdmin = (uid, email) => {
  const adminUid = import.meta.env.VITE_ADMIN_UID;
  const adminEmail = import.meta.env.VITE_ADMIN_EMAIL;
  
  return uid === adminUid || email === adminEmail;
};

/**
 * Get approval status badge class
 * @param {string} status - Approval status
 * @returns {string} CSS class name
 */
export const getApprovalStatusClass = (status) => {
  const classes = {
    pending: 'badge-warning',
    approved: 'badge-success',
    rejected: 'badge-danger'
  };
  return classes[status] || 'badge-secondary';
};

/**
 * Get payment status badge class
 * @param {string} status - Payment status
 * @returns {string} CSS class name
 */
export const getPaymentStatusClass = (status) => {
  const classes = {
    pending: 'badge-warning',
    success: 'badge-success',
    failed: 'badge-danger'
  };
  return classes[status] || 'badge-secondary';
};

/**
 * Scroll to top of page
 */
export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * Scroll to element by ID
 * @param {string} elementId - Element ID
 */
export const scrollToElement = (elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};
